import { ThemeName } from '@design/design-tokens/themes/themes';
import { useWindowResize } from '@helpers';
import { getInstanceByDom, init, registerMap } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useRef } from 'react';

import {
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
} from './apache-config';
import { usaData } from './data/usaMap';

export interface ApacheChartProps extends Types.EChartsOption {
  /**
   * Discriminator string used by the wrapper to apply chart-type-specific
   * setup. Currently `'apache-us-map'` registers the USA GeoJSON; other
   * values are treated as plain echarts options.
   */
  type: string;
  /** Design-system theme registered with echarts. Defaults to `'dark_default'`. */
  theme?: ThemeName;
  // Inherits every Apache ECharts option (`series`, `xAxis`, `yAxis`,
  // `tooltip`, `grid`, `dataset`, `legend`, …) — see
  // https://echarts.apache.org/en/option.html for the full reference.
}

/**
 * Generic [Apache ECharts](https://echarts.apache.org/) wrapper used as
 * the engine for nearly every chart in the `Charts/Apache/*` Storybook
 * section (candlestick, OHLC, technicals, US map, stacked area / bar,
 * box-and-whisker, etc.).
 *
 * Spread any echarts option directly as props (`series`, `xAxis`,
 * `yAxis`, `tooltip`, `grid`, `legend`, `dataset`, …); they're merged
 * into the design-system base config and re-applied on every prop
 * change. The wrapper handles container resizing, theme initialisation,
 * and `'apache-us-map'`-specific GeoJSON registration.
 *
 * For the higher-level "plot family" charts (bar / line / sankey /
 * heatmap / tree-map / dot / bump / doughnut / waterfall), use the
 * dedicated wrappers under the matching `*-plots/` subfolder — they
 * encode opinionated defaults on top of `ApacheChart`.
 */
export const ApacheChart = ({
  theme = 'dark_default',
  type,
  ...options
}: ApacheChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    if (type === 'apache-us-map') {
      registerMap('USA', usaData as any, {
        Alaska: {
          left: -131,
          top: 25,
          width: 15,
        },
        Hawaii: {
          left: -110,
          top: 28,
          width: 5,
        },
        'Puerto Rico': {
          left: -76,
          top: 26,
          width: 2,
        },
      });
    }

    const chartWidth = ref.current?.clientWidth || DEFAULT_CHART_WIDTH;
    const chartDom = init(ref.current, theme, {
      height: DEFAULT_CHART_HEIGHT,
      width: chartWidth,
    });

    if (Object.keys(options).length) {
      const option = createApacheConfig({
        theme,
        options,
      });
      chartDom.setOption(option);
    }

    return () => {
      chartDom.dispose();
    };
  }, []);

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      const option = createApacheConfig({
        theme,
        options,
      });
      chartDom?.setOption(option);
    }
  }, [theme, type, options]);

  return <div ref={ref} />;
};
